import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { salonAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Download, QrCode, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function QRCodePage() {
    const { user } = useAuth();
    const [bookingUrl, setBookingUrl] = useState("");
    
    useEffect(() => {
        // Generate booking URL based on current domain
        const baseUrl = window.location.origin;
        setBookingUrl(`${baseUrl}/book/${user?.id}`);
    }, [user]);

    const copyLink = () => {
        navigator.clipboard.writeText(bookingUrl);
        toast.success("Link copied to clipboard!");
    };

    const downloadQR = () => {
        const svg = document.getElementById("qr-code");
        if (!svg) return;
        
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();
        
        img.onload = () => {
            canvas.width = 400;
            canvas.height = 400;
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, 400, 400);
            
            const pngFile = canvas.toDataURL("image/png");
            const downloadLink = document.createElement("a");
            downloadLink.download = "booking-qrcode.png";
            downloadLink.href = pngFile;
            downloadLink.click();
        };
        
        img.src = "data:image/svg+xml;base64," + btoa(svgData);
        toast.success("QR Code downloaded!");
    };

    const shareLink = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Book at ${user?.business_name}`,
                    text: `Book your appointment at ${user?.business_name}`,
                    url: bookingUrl,
                });
            } catch (error) {
                copyLink();
            }
        } else {
            copyLink();
        }
    };

    return (
        <div className="p-4 md:p-8 lg:p-12 space-y-6" data-testid="qrcode-page">
            <div>
                <h1 className="font-heading text-3xl font-bold">Online Booking</h1>
                <p className="text-muted-foreground">Share your booking link with clients</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* QR Code Card */}
                <Card className="border-border/40 shadow-card">
                    <CardHeader className="text-center">
                        <CardTitle className="font-heading">Your Booking QR Code</CardTitle>
                        <CardDescription>
                            Clients can scan this to book appointments instantly
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center">
                        <div className="p-6 bg-white rounded-2xl shadow-lg mb-6">
                            <QRCodeSVG
                                id="qr-code"
                                value={bookingUrl}
                                size={200}
                                level="H"
                                fgColor="#A05E2B"
                                includeMargin={true}
                            />
                        </div>
                        <p className="text-center text-sm text-muted-foreground mb-4">
                            {user?.business_name}
                        </p>
                        <Button onClick={downloadQR} className="w-full max-w-xs" data-testid="download-qr-btn">
                            <Download className="w-4 h-4 mr-2" />
                            Download QR Code
                        </Button>
                    </CardContent>
                </Card>

                {/* Link Sharing Card */}
                <Card className="border-border/40 shadow-card">
                    <CardHeader>
                        <CardTitle className="font-heading">Booking Link</CardTitle>
                        <CardDescription>
                            Share this link on social media or send directly to clients
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex gap-2">
                            <Input
                                value={bookingUrl}
                                readOnly
                                className="bg-secondary/50"
                                data-testid="booking-url-input"
                            />
                            <Button variant="outline" size="icon" onClick={copyLink} data-testid="copy-link-btn">
                                <Copy className="w-4 h-4" />
                            </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <Button variant="outline" onClick={shareLink} className="w-full">
                                <Share2 className="w-4 h-4 mr-2" />
                                Share
                            </Button>
                            <Button variant="outline" asChild className="w-full">
                                <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Preview
                                </a>
                            </Button>
                        </div>

                        <div className="pt-4 space-y-4">
                            <h3 className="font-medium">Where to use your QR Code</h3>
                            <div className="grid gap-3">
                                <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                                    <QrCode className="w-5 h-5 text-primary mt-0.5" />
                                    <div>
                                        <p className="font-medium">Reception Counter</p>
                                        <p className="text-sm text-muted-foreground">Print and display at your front desk</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                                    <QrCode className="w-5 h-5 text-primary mt-0.5" />
                                    <div>
                                        <p className="font-medium">Business Cards</p>
                                        <p className="text-sm text-muted-foreground">Add to your business cards for easy booking</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                                    <QrCode className="w-5 h-5 text-primary mt-0.5" />
                                    <div>
                                        <p className="font-medium">Social Media</p>
                                        <p className="text-sm text-muted-foreground">Share on Instagram, Facebook, WhatsApp</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                                    <QrCode className="w-5 h-5 text-primary mt-0.5" />
                                    <div>
                                        <p className="font-medium">Window Display</p>
                                        <p className="text-sm text-muted-foreground">Print large format for your shop window</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
